import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Input, message } from 'antd';
import classnames from 'classnames';
import { useRouter } from 'next/navigation';
import type { SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import MindverseModal from '@/components/modal';
import TitleDescription from '@/components/title-description';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { proposeAccountDeletion } from '@/service/account';
import { ROUTER_PATH, prefetch } from '@/utils/router';

import styles from './index.less';

export default function DeleteAccount(): JSX.Element {
  const confirmText = 'Delete account';
  const router = useRouter();
  const [open, setOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [layoutSize] = useStorage('layoutSize');

  const { t } = useTranslation();

  const deleteAccount = () => {
    setOpen(true);
  };

  const proposeDelete = () => {
    setDeleteLoading(true);
    proposeAccountDeletion()
      .then((res) => {
        const { data } = res;

        if (data.code === 0) {
          router.push(ROUTER_PATH.over);
          message.success('Your account will be deleted after 7 days');
        } else {
          throw res;
        }
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const handleChange = (e: { target: { value: SetStateAction<string> } }) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    prefetch(router, ROUTER_PATH.over);
  }, [router]);

  return (
    <>
      {layoutSize === LAYOUT_SIZE.SMALL ? (
        <div className={styles.settingItem} onClick={() => deleteAccount()}>
          <div className={classnames(styles.settingInfo, 'text-[#ff4d4f]')}>
            <ExclamationCircleFilled />
            <div>{t('menu.accountSettingsModal.deleteAccount')}</div>
          </div>
        </div>
      ) : (
        <>
          <TitleDescription
            className={styles.titleItem}
            title={t('menu.accountSettingsModal.deleteAccount')}
          />
          <Button
            className={styles.delete}
            danger
            ghost={true}
            onClick={() => deleteAccount()}
            type="primary"
          >
            {t('menu.accountSettingsModal.deleteAccountBtn')}
          </Button>
        </>
      )}
      <MindverseModal
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="delete"
            danger
            disabled={value != confirmText}
            loading={deleteLoading}
            onClick={() => proposeDelete()}
            type="primary"
          >
            {t('common.delete')}
          </Button>
        ]}
        onCancel={() => setOpen(false)}
        open={open}
        title={confirmText}
        width={580}
      >
        <div className={styles.deleteConfirm}>
          <div className={styles.desc}>
            Your account will be deleted in <span className={styles.strongText}>7 days after</span>{' '}
            confirming the deletion. During this period, you can cancel the deletion here.
          </div>
          <ul className={styles.ulContent}>
            <li>After your account is deleted, all your data will be completely erased. </li>
            <li>
              Please note that deleting your account is irreversible, and the data cannot be
              restored or undone.
            </li>
          </ul>
          <div>Are you sure you want to delete your account?</div>
          <div
            className={styles.confirmText}
          >{`Please type "${confirmText}" below to confirm the operation.`}</div>
          <Input onChange={handleChange} placeholder={confirmText} value={value} />
        </div>
      </MindverseModal>
    </>
  );
}
